import React from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  cn,
  Flex,
  GaugeChart,
  Heading,
  LoadingOrbit,
  StackedBarChart,
  Text,
  chartThemes,
  useTheme,
} from '@chatsdet/ui';
import { ChartBar } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useWorkflowTestExecutions } from '../../../features/workflows/useWorkflowTestExecutions';
import {
  buildTestExecutionChartData,
  latestTestExecutionPassRate,
  type TestExecutionChartDataPoint,
} from './testExecutionChartData';

export function TestExecutionChart({ className, orgId }: { className?: string; orgId: string }) {
  const navigate = useNavigate();
  const { config } = useTheme();
  const chartColors = chartThemes[config.theme];
  const query = useWorkflowTestExecutions(orgId, { pageSize: 30 });

  const TARGET_BAR_COUNT = 30;

  const chartData = React.useMemo(() => {
    if (!query.data?.data) return [];
    const real = buildTestExecutionChartData(query.data.data);
    const padCount = Math.max(0, TARGET_BAR_COUNT - real.length);
    const padding: TestExecutionChartDataPoint[] = Array.from({ length: padCount }, (_, i) => ({
      id: '',
      kind: 'cofactor',
      index: -(padCount - i),
      date: '',
      passed: 0,
      failed: 0,
      running: 0,
      flaky: 0,
      status: 'completed' as const,
      isComplete: false,
    }));
    return [...padding, ...real];
  }, [query.data]);

  const latestPassRate = React.useMemo(() => {
    return latestTestExecutionPassRate(chartData);
  }, [chartData]);

  if (query.isLoading) {
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex align="center" justify="center" direction="column" gap="sm" className="py-4">
            <LoadingOrbit size="lg" />
            <Text size="sm" className="text-muted-foreground">
              Loading test runs...
            </Text>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex direction="column" gap="md" align="start">
            <div>
              <Heading level={4}>Unable to load test runs</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                We couldn&apos;t fetch the latest data. Try again in a moment.
              </Text>
            </div>
            <Button variant="outline" onPress={() => query.refetch()}>
              Retry
            </Button>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return null;
  }

  // Centralized brand axis: pass = success (azure), fail = destructive (warm
  // orange), running = muted. Use the named semantic tokens, not positional
  // palette slots (colors[2] is emerald in the cofactor theme).
  const passedColor = chartColors.success;
  const failedColor = chartColors.destructive;
  const runningColor = chartColors.muted;

  return (
    <Card padding="lg" className={cn(className)}>
      <CardHeader>
        <Flex align="center" gap="sm">
          <ChartBar size={18} />
          <Heading level={4}>Test Runs</Heading>
        </Flex>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
          <div className="min-w-0 flex-1">
            <StackedBarChart
              height={160}
              data={chartData}
              xDataKey="date"
              stacks={[
                { dataKey: 'passed', name: 'Passed', color: passedColor },
                { dataKey: 'failed', name: 'Failed', color: failedColor },
                { dataKey: 'running', name: 'In Progress', color: runningColor },
              ]}
              onBarClick={(data) => {
                const point = data as TestExecutionChartDataPoint;
                if (point.id) {
                  if (point.kind === 'external') {
                    navigate({ to: '/runs/external/$runId', params: { runId: point.id } });
                  } else {
                    navigate({ to: '/runs/test/$executionId', params: { executionId: point.id } });
                  }
                }
              }}
              showYAxis={false}
              showLegend={false}
              // No `barSize` cap on purpose. This chart pads to a fixed slot
              // count already, so bars filling their slot is the intended
              // look. `barSize` was a no-op until it was wired up in
              // StackedBarChart, so the 16 that used to sit here would now
              // narrow bars that have always rendered wider.
              renderTooltip={(data) => {
                const point = data as TestExecutionChartDataPoint;
                const total = point.passed + point.failed + point.running;
                const isRunning =
                  point.status === 'running' ||
                  point.status === 'queued' ||
                  point.status === 'open' ||
                  point.status === 'collecting';
                return (
                  <div className="space-y-1">
                    <Text size="sm" weight="medium">
                      {point.date}
                      {point.kind === 'external' && (
                        <span className="ml-1.5 text-muted-foreground font-normal">External</span>
                      )}
                      {isRunning && (
                        <span className="ml-1.5 text-muted-foreground font-normal">Running</span>
                      )}
                    </Text>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-sm"
                        style={{ backgroundColor: passedColor }}
                      />
                      <Text size="sm">
                        Passed: {point.passed}
                        {point.flaky > 0 ? ` (${point.flaky} flaky)` : ''}
                      </Text>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-sm"
                        style={{ backgroundColor: failedColor }}
                      />
                      <Text size="sm">Failed: {point.failed}</Text>
                    </div>
                    {point.running > 0 && (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-sm"
                          style={{ backgroundColor: runningColor }}
                        />
                        <Text size="sm">In Progress: {point.running}</Text>
                      </div>
                    )}
                    <Text size="xs" className="text-muted-foreground">
                      Total: {total}
                    </Text>
                  </div>
                );
              }}
            />
          </div>

          <div className="flex shrink-0 items-center justify-center self-center">
            <GaugeChart
              score={latestPassRate}
              label="Pass Rate"
              size="sm"
              strokeWidth={6}
              showScore={true}
              showLabel={true}
              labelClassName="mt-0.5"
              color={passedColor}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
